import React from 'react';
import ReactECharts from 'echarts-for-react';

const PDFFullChart = ({ design1CO2eq, design2CO2eq, design1title, design2title  }) => {
    if (!design1CO2eq.length || !design2CO2eq.length) {
        return <div>Loading...</div>;
    }

    // Function to generate PDF data for lognormal distribution
    const logNormalPDF = (x, mu, sigma) => {
        if (x <= 0) return 0;
        return (
            (1 / (x * sigma * Math.sqrt(2 * Math.PI))) *
            Math.exp(-((Math.log(x) - mu) ** 2) / (2 * sigma ** 2))
        );
    };

    // Function to calculate log-normal parameters (mu and sigma)
    const calculateLogNormalParams = (data) => {
        const logData = data.map((x) => Math.log(x));
        const mu = logData.reduce((acc, val) => acc + val, 0) / logData.length;
        const variance = logData.reduce((acc, val) => acc + (val - mu) ** 2, 0) / logData.length;
        const sigma = Math.sqrt(variance);
        return { mu, sigma };
    };

    // Function to generate PDF data for plotting
    const generatePDFData = (xRange, mu, sigma) => {
        return xRange.map((x) => ({
            value: logNormalPDF(x, mu, sigma),
            x: x,
        }));
    };

    // Function to calculate histogram bins
    const calculateHistogramBins = (data, numBins) => {
        const min = Math.min(...data);
        const max = Math.max(...data);
        const binWidth = (max - min) / numBins;
        const bins = Array(numBins).fill(0).map((_, i) => ({
            x: min + i * binWidth,
            value: 0,
        }));

        data.forEach((value) => {
            const binIndex = Math.min(Math.floor((value - min) / binWidth), numBins - 1);
            bins[binIndex].value += 1;
        });

        return bins;
    };

    // Process data to extract the last CO2eq values
    const CO2eqCumsum1 = design1CO2eq.map(arr => arr[arr.length - 1].reduce((acc, val) => acc + val, 0) / 1e6);
    const CO2eqCumsum2 = design2CO2eq.map(arr => arr[arr.length - 1].reduce((acc, val) => acc + val, 0) / 1e6);

    // Calculate log-normal distribution parameters
    const { mu: mu1, sigma: sigma1 } = calculateLogNormalParams(CO2eqCumsum1);
    const { mu: mu2, sigma: sigma2 } = calculateLogNormalParams(CO2eqCumsum2);

    // Define the range of values to plot (shift the start to a small positive value)
    const xRange1 = Array.from({ length: 100 }, (_, i) => 0.1 + i * 0.1);  // Start from 0.1, with a step of 0.1
    const xRange2 = Array.from({ length: 100 }, (_, i) => 0.1 + i * 0.1);

    // Generate PDF data using lognormal PDF function
    const pdfData1 = generatePDFData(xRange1, mu1, sigma1);
    const pdfData2 = generatePDFData(xRange2, mu2, sigma2);

    // Calculate global min and max for x-axis
    const globalMin = Math.min(Math.min(...CO2eqCumsum1), Math.min(...CO2eqCumsum2));
    const globalMax = Math.max(Math.max(...CO2eqCumsum1), Math.max(...CO2eqCumsum2));

    // Histogram data (binning CO2eq data)
    const histogramBins1 = calculateHistogramBins(CO2eqCumsum1, 20); // Adjust bin size (20) as needed
    const histogramBins2 = calculateHistogramBins(CO2eqCumsum2, 20); // Adjust bin size (20) as needed

    // Function to normalize the histogram
    const normalizeHistogram = (histogramBins, binWidth) => {
        const totalFrequency = histogramBins.reduce((acc, bin) => acc + bin.value, 0);
        return histogramBins.map(bin => ({
            ...bin,
            value: bin.value / (totalFrequency * binWidth)  // Normalize by total frequency and bin width
        }));
    };

    // Calculate bin width for normalization
    const binWidth1 = (Math.max(...CO2eqCumsum1) - Math.min(...CO2eqCumsum1)) / 20;
    const binWidth2 = (Math.max(...CO2eqCumsum2) - Math.min(...CO2eqCumsum2)) / 20;

    // Normalize histograms
    const normalizedHistogramBins1 = normalizeHistogram(histogramBins1, binWidth1);
    const normalizedHistogramBins2 = normalizeHistogram(histogramBins2, binWidth2);

    // Scale PDFs to be visible along with the normalized histograms
    const scaleFactor1 = Math.max(...normalizedHistogramBins1.map(bin => bin.value)) / Math.max(...pdfData1.map(data => data.value));
    const scaleFactor2 = Math.max(...normalizedHistogramBins2.map(bin => bin.value)) / Math.max(...pdfData2.map(data => data.value));

    const scaledPdfData1 = pdfData1.map((data) => ({
        ...data,
        value: data.value * scaleFactor1,
    }));

    const scaledPdfData2 = pdfData2.map((data) => ({
        ...data,
        value: data.value * scaleFactor2,
    }));

    // Define ECharts option with updated data
    const option = {
        title: {
            text: 'Probabilistic Distribution of Life Cycle GHG Emission',
            left: 'center',
        },
        tooltip: {
            trigger: 'axis',
        },
        legend: {
            data: [`${design1title} PDF`, `${design2title} PDF`, `${design1title} Histogram`, `${design2title} Histogram`],
            top: '10%',
        },
        xAxis: {
            type: 'value',
            nameLocation: 'middle',
            nameGap: 30,
            name: 'Life Cycle GHG Emission (CO2-eq kt)',
            min: globalMin,
            max: globalMax,
            axisLabel: {
                fontSize: 14,
                formatter: (value) => {
                    return value.toFixed(1); // Keep one decimal place
                },
            },
        },
        yAxis: {
            type: 'value',
            nameLocation: 'middle',
            nameGap: 30,
            name: 'Probability Density',
            axisLabel: {
                fontSize: 14,
            },
        },
        series: [
            // Normalized Histogram for Design 1
            {
                name: `${design1title} Histogram`,
                type: 'bar',
                data: normalizedHistogramBins1.map(({ x, value }) => [x, value]),
                itemStyle: {
                    color: '#0095D9',
                    borderColor: '#000000',
                    borderWidth: 1,
                },
                barWidth: 10,
                opacity: 0.8,
                z: 1,
                yAxisIndex: 0, // Use the main y-axis
            },
            // Normalized Histogram for Design 2
            {
                name: `${design2title} Histogram`,
                type: 'bar',
                data: normalizedHistogramBins2.map(({ x, value }) => [x, value]),
                itemStyle: {
                    color: '#F08050',
                    borderColor: '#000000',
                    borderWidth: 1,
                },
                barWidth: 10,
                opacity: 0.8,
                z: 1,
                yAxisIndex: 0, // Use the main y-axis
            },
            // Scaled PDF for Design 1
            {
                name: `${design1title} PDF`,
                type: 'line',
                data: scaledPdfData1.map((value, index) => [xRange1[index], value.value]),
                color: '#0072B2',
                lineStyle: {
                    width: 1.5,
                },
                smooth: true,
                z: 2,
                yAxisIndex: 0, // Use the main y-axis
            },
            // Scaled PDF for Design 2
            {
                name: `${design2title} PDF`,
                type: 'line',
                data: scaledPdfData2.map((value, index) => [xRange2[index], value.value]),
                color: '#D95319',
                lineStyle: {
                    width: 1.5,
                },
                smooth: true,
                z: 2,
                yAxisIndex: 0, // Use the main y-axis
            },
        ],
    };

    return <ReactECharts option={option} style={{ height: '500px', width: '100%' }} />;
};

export default PDFFullChart;
